import { createElement, useEffect, useRef } from "react";

export const TICKET_W = 440;
export const TICKET_H = 596;

const INK = "#f47c7c";
const TEXT = "#1d1d1d";
const FONT = "Arial, Helvetica, sans-serif";
const FONTSIZE = 28;

export const emptyTicket = {
  callDate: "",
  jobDate: "",
  eta: "",
  name: "",
  address: "",
  zipCode: "",
  unitNum: "",
  community: "",
  gateCode: "",
  phoneNum: "",
  email: "",
  jobType: "",
  plumberSelected: false,
  selectedPlumbers: [],
  estJobTime: "",
  paymentType: "",
  jobNotes1: "",
  jobNotes2: "",
  jobNotes3: "",
  jobNotes4: "",
  jobNotes5: "",
  jobNotes6: "",
  jobNotes7: "",
  comments1: "",
  comments2: "",
};

function fitText(ctx, text, maxW, size, weight = "normal") {
  let s = size;
  ctx.font = `${weight} ${s}px ${FONT}`;
  while (s > 8 && ctx.measureText(text).width > maxW) {
    s -= 0.5;
    ctx.font = `${weight} ${s}px ${FONT}`;
  }
  return text;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Draws one ticket onto a 2D context sized TICKET_W x TICKET_H (already scaled). */
export function drawTicket(ctx, input) {
  const d = { ...emptyTicket, ...input };
  ctx.clearRect(0, 0, TICKET_W, TICKET_H);
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, TICKET_W, TICKET_H);

  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.textBaseline = "alphabetic";

  // Outer border
  ctx.lineWidth = 3;
  roundRect(ctx, 3, 3, TICKET_W - 6, TICKET_H - 6, 6);
  ctx.stroke();

  // Top-left job type box
  roundRect(ctx, 3, 3, 139, 27, 6);
  ctx.stroke();
  if (d.jobType) {
    ctx.fillStyle = TEXT;
    fitText(ctx, d.jobType, 125, 22, "bold"); // Job Type font size
    ctx.fillText(d.jobType, 10, 24); // change location of input text
  }

  const line = (x1, x2, y) => {
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(x1, y);
    ctx.lineTo(x2, y);
    ctx.stroke();
  };

  // label at x, line from x1..x2 at y, value drawn on the line
  const field = (label, lx, x1, x2, y, value) => {
    ctx.fillStyle = INK;
    ctx.font = `15px ${FONT}`; // font adjustment?
    ctx.fillText(label, lx, y - 4);
    line(x1, x2, y);
    if (value) {
      ctx.fillStyle = TEXT;
      fitText(ctx, String(value), x2 - x1 - 6, FONTSIZE); // adjust last value (28) for font size
      ctx.fillText(String(value), x1 + 3, y - 4);
    }
  };

  const plumbers = (d.selectedPlumbers || []).map((p) => p.value).join(", ");

  field("Plumber", 147, 209, 293, 34, plumbers);
  field("Gate", 298, 337, 431, 34, d.gateCode);
  field("Call Date", 7, 76, 243, 67, d.callDate);
  field("Est. Job Time", 258, 357, 431, 67, d.estJobTime);
  field("Job Date", 7, 74, 303, 101, d.jobDate);
  field("ETA", 314, 350, 433, 101, d.eta);
  field("Name", 7, 59, 433, 136, d.name);
  field("Community", 7, 90, 310, 170, d.community);
  field("ZIP", 319, 348, 433, 170, d.zipCode);
  field("Address", 7, 69, 308, 204, d.address);
  field("Unit #", 313, 359, 433, 204, d.unitNum);

  // Job notes: first line shares the label row, then 6 more lines
  field("Job Notes", 7, 81, 435, 239, d.jobNotes1);
  const noteYs = [273, 307, 341, 376, 410, 444];
  const notes = [
    d.jobNotes2,
    d.jobNotes3,
    d.jobNotes4,
    d.jobNotes5,
    d.jobNotes6,
    d.jobNotes7,
  ];
  noteYs.forEach((y, i) => {
    line(7, 433, y);
    if (notes[i]) {
      ctx.fillStyle = TEXT;
      fitText(ctx, notes[i], 420, FONTSIZE); // font size adjustment
      ctx.fillText(notes[i], 10, y - 4);
    }
  });

  // Office divider
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(3, 453);
  ctx.lineTo(190, 453);
  ctx.moveTo(250, 453);
  ctx.lineTo(TICKET_W - 3, 453);
  ctx.stroke();
  ctx.fillStyle = INK;
  ctx.font = `bold 15px ${FONT}`;
  ctx.textAlign = "center";
  ctx.fillText("Office", 220, 459);
  ctx.textAlign = "left";

  field("Phone", 7, 57, 276, 485, d.phoneNum);
  field("Payment", 281, 346, 433, 485, d.paymentType);
  field("Email", 7, 50, 433, 519, d.email);
  field("Comments", 7, 87, 433, 553, d.comments1);
  line(7, 433, 580);
  if (d.comments2) {
    ctx.fillStyle = TEXT;
    fitText(ctx, d.comments2, 420, FONTSIZE);
    ctx.fillText(d.comments2, 10, 576);
  }
}

/**
 * Live canvas preview of one ticket.
 * Props:
 *  - data: ticket fields (see emptyTicket)
 *  - width: CSS width (number = px, or any CSS string like "4in"). Height keeps aspect ratio.
 *  - pixelRatio: canvas resolution multiplier (default: devicePixelRatio, min 2). Use 3+ for print.
 */
export default function TicketCanvas({
  data,
  width = 440,
  pixelRatio,
  style,
  className,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ratio = pixelRatio || Math.max(2, window.devicePixelRatio || 1);
    canvas.width = TICKET_W * ratio;
    canvas.height = TICKET_H * ratio;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    drawTicket(ctx, data || {});
  }, [data, pixelRatio]);

  const cssW = typeof width === "number" ? `${width}px` : width;
  return createElement("canvas", {
    ref,
    className,
    style: {
      width: cssW,
      aspectRatio: `${TICKET_W} / ${TICKET_H}`,
      display: "block",
      ...style,
    },
  });
}
